import type { RngState, Seat } from "@bing/engine-core";

/* ------------------------------------------------------------------ *
 * دوز — Connect Four on the canonical 7×6 grid.
 *
 * The geometry lives here as named constants rather than as magic
 * numbers scattered through the rules: getting 7×6 or the four
 * directions wrong produces a DIFFERENT game that still looks right.
 * ------------------------------------------------------------------ */

/** Columns across the board. */
export const COLS = 7;
/** Rows up the board. */
export const ROWS = 6;
/** Cells on the board. */
export const CELLS = COLS * ROWS;
/** How many in a row wins a hand. */
export const CONNECT = 4;
/** دوز is a two-player game — always exactly two seats. */
export const DOOZ_SEATS = 2;

/**
 * An EMPTY cell. Anything else is the {@link Seat} that owns the piece, so a cell is read
 * with a single comparison and never needs a nullable.
 */
export const EMPTY = -1;

/**
 * ⚠️ ROW 0 IS THE **BOTTOM** ROW. `cell = row * COLS + col`.
 *
 * Gravity is the whole point of this game, and it is far easier to get right when "falls to
 * the lowest empty cell" is literally "the smallest empty row index in that column" — which
 * is also `heights[col]`. The CLIENT draws row 0 last (at the bottom of the board); nothing
 * in the rules ever thinks in screen order.
 */
export function cellAt(row: number, col: number): number {
  return row * COLS + col;
}

/** A player intent. */
export type DoozMove =
  /** Drop a piece into `col`; it falls to the lowest empty cell. Your turn, column not full. */
  | { readonly kind: "drop"; readonly col: number }
  /**
   * THROW YOUR OWN OPENING DIE. Legal only while {@link DoozOpeningRoll} is live and only for a
   * seat that has not thrown yet — so, like `nextHand`, it is legal for BOTH seats at once and in
   * either order. Higher opens hand 1; a tie is thrown again.
   */
  | { readonly kind: "openingRoll" }
  /**
   * Deal the next دست of a match once a hand has been decided.
   *
   * ⚠️ LEGAL FOR **EITHER** SEAT, which is what makes the between-hands pause a SIMULTANEOUS
   * phase rather than somebody's turn (تخته نرد's `nextRound` idiom). The online table then runs
   * ONE shared window for it — and a shared window's expiry auto-plays without charging an idle
   * miss, so nobody is ever penalised for reading the winning line.
   */
  | { readonly kind: "nextHand" };

/** Hand counts a دوز match may run to. A PUBLIC table is always 3; a private host picks. */
export const DOOZ_HAND_COUNTS = [3, 5] as const;
export type DoozHandCount = (typeof DOOZ_HAND_COUNTS)[number];
/** The conventional match — and what a public table always plays. */
export const DEFAULT_DOOZ_HANDS: DoozHandCount = 3;

/**
 * THE OPENING CONTEST — each seat throws ONE die, higher opens hand 1, a tie is thrown again.
 *
 * ⚠️ THE DICE ARE THROWN BY THE PLAYERS, NOT BY `createInitialState`. They used to be rolled
 * inside the deal, so the throw that decided who started was made before anybody saw it and all
 * that reached the screen was a banner stating the result — «no dice appear at all». It is a real
 * phase now, the same shape as تخته نرد's §5.1 contest.
 */
export interface DoozOpeningRoll {
  /** Each seat's own single die — null until they throw it. */
  readonly dice: readonly [number | null, number | null];
  /** How many ties have been re-thrown. Display only; a tie is simply thrown again. */
  readonly ties: number;
  /**
   * The pair that JUST TIED, held until somebody throws again — display only.
   *
   * ⚠️ WITHOUT THIS THE SECOND PLAYER NEVER SEES THEIR OWN THROW. The contest resolves inside the
   * SAME `applyMove` that rolls the second die, so no intermediate state carries it; on a tie that
   * call writes `dice: [null, null]` and the value would never be transmitted at all. تخته نرد
   * shipped without it and the report was «their die is never displayed and the game announces a
   * tie» — the same defect, avoided here by construction.
   */
  readonly tied?: readonly [number, number] | null;
}

/** Rule flags. There is exactly one, and it is the match length. */
export interface DoozConfig {
  /**
   * The match's FULL LENGTH — the most hands it can run to, not the number it always plays.
   *
   * ⚠️ IT STOPS AS SOON AS THE RESULT IS SETTLED. 3–0 after three of five ends there: with two
   * hands left the trailer can gain at most two and the lead is three, so nothing remaining can
   * change who won. 2–1 with two to play carries on, because the trailer CAN still level it and a
   * level match is its own result. This used to run every hand regardless, to keep «over 5 hands
   * the die's winner opens 3 and their opponent 2» literally true; that symmetry is invisible and
   * dead rubbers are not. Operator decision, 2026-09-03.
   */
  readonly handsToPlay: number;
}

export const DEFAULT_DOOZ_CONFIG: DoozConfig = { handsToPlay: DEFAULT_DOOZ_HANDS };

/** The result of a finished دست, held until the next one is dealt. */
export interface DoozHandResult {
  /** The seat that made four in a row, or `null` for a full board with no four (a draw). */
  readonly winner: Seat | null;
  /**
   * The cells of the winning line, ascending. `CONNECT` long, or longer when the drop completed
   * a run of five or six — the client lights exactly what it is given rather than re-deriving it
   * (the second copy of a direction table is how a highlight starts disagreeing with the rules).
   * Empty on a draw.
   */
  readonly line: readonly number[];
}

/**
 * Authoritative دوز state — plain JSON, serializable to `ActiveGame.state` (solo).
 *
 * ⚠️ NOTHING HERE IS A TYPED ARRAY OR A BigInt. A solo game is persisted as JSON and rehydrated
 * from it, so the state has to survive `JSON.parse(JSON.stringify(state))` unchanged. The search
 * inside the bot builds its own fast mirror; that mirror never reaches the state.
 */
export interface DoozState {
  /** 42 cells, `cell = row * COLS + col`, row 0 = BOTTOM. {@link EMPTY} or the owning seat. */
  readonly cells: readonly number[];
  /**
   * Pieces already in each column — i.e. the row the NEXT piece there would land in. Derived
   * from `cells`, and carried so `applyMove` and `legalMoves` are O(COLS) rather than O(CELLS).
   * {@link rebuildHeights} recomputes it, which is what a rehydrated legacy row is repaired with.
   */
  readonly heights: readonly number[];
  readonly turn: Seat;
  /** 1-based دست number within the match. */
  readonly hand: number;
  readonly config: DoozConfig;
  /**
   * WHO OPENED HAND 1 — decided by the contest, and the anchor for every later hand: hand `h` is
   * opened by `(firstMover + h - 1) % 2`. Stored rather than recomputed so the alternation has
   * exactly one definition ({@link openerOf}).
   *
   * ⚠️ MEANINGLESS WHILE {@link openingRoll} IS LIVE — it holds a placeholder 0 until the contest
   * resolves, exactly as تخته نرد's `turn` does. Nothing may act in that phase but `openingRoll`
   * itself, and `viewFor` withholds `legalCols` there, so no reader can act on the placeholder.
   */
  readonly firstMover: Seat;
  /**
   * THE LIVE OPENING CONTEST, or null once it has been decided.
   *
   * ⚠️ OPTIONAL, and every reader treats `undefined` as null: a solo `ActiveGame` row persisted
   * before the contest existed has no such field and is already PAST its opening, so a legacy
   * resume must fall straight through to normal play — never into a contest it can never finish.
   * Same defaulting rule تخته نرد uses for `matchTo`/`score`/`round`/`pendingRound`.
   */
  readonly openingRoll?: DoozOpeningRoll | null;
  /**
   * The two dice that DECIDED {@link firstMover} — seat 0's first. Display only: the announcement
   * that closes the contest shows the throw that won it. They can never be equal (a tie is thrown
   * again), so the reader never has to explain a draw.
   *
   * OPTIONAL because it does not exist until the contest resolves, and because a state hand-built
   * from a fixture has no ceremony to show; every reader treats `undefined` as "no ceremony".
   */
  readonly opening?: readonly [number, number];
  /** Hands won, per seat. A DRAWN hand pays neither side. */
  readonly wins: readonly [number, number];
  /** Hands that ended with a full board and no four. */
  readonly draws: number;
  /**
   * Set the moment a دست is decided and the MATCH is not yet over. While it is set the board
   * holds that hand's final position and the only legal move — for EITHER seat — is `nextHand`,
   * so both players get to see how it ended before the next one is dealt.
   */
  readonly pendingHand: DoozHandResult | null;
  /** The hand that ENDED the match, so the final board keeps its winning line lit. */
  readonly finalHand: DoozHandResult | null;
  /** The last piece dropped, for the fall animation and the last-move marker. */
  readonly lastDrop: DoozDrop | null;
  /**
   * MONOTONIC count of every applied move. The client keys its fall animation on THIS, never on
   * `lastDrop`'s object identity: every frame arrives as fresh JSON over the socket, so the drop
   * is a NEW object with identical contents on every unrelated state update — comparing identity
   * re-fires the fall and its sound on the next frame that has nothing to do with it. Never resets.
   */
  readonly moveCount: number;
  readonly rngState: RngState;
  /** The MATCH winner. Null while the match is live, INCLUDING between hands — and null on a
   *  finished match that ENDED LEVEL (see {@link DoozState.tiedMatch}). */
  readonly winner: Seat | null;
  /** True when the match is over and the two seats finished with the same number of hands won. */
  readonly tiedMatch: boolean;
}

/** One dropped piece. */
export interface DoozDrop {
  readonly seat: Seat;
  readonly col: number;
  readonly row: number;
  readonly cell: number;
}

/**
 * The per-seat projection. دوز is PERFECT INFORMATION, so nothing is redacted — the view exists
 * to give the client the derived facts it would otherwise recompute (and get subtly wrong): which
 * columns are playable, and exactly which cell a piece would land in.
 */
export interface DoozView {
  readonly you: Seat;
  readonly turn: Seat;
  readonly cells: readonly number[];
  /**
   * Columns YOU may drop into right now — empty when it is not your turn, when a hand is waiting
   * to be dealt, or when the match is over.
   */
  readonly legalCols: readonly number[];
  /**
   * Per column, the ROW a piece would land in, or `-1` for a full column.
   *
   * ⚠️ THE PREVIEW READS THIS RATHER THAN COUNTING PIECES ITSELF. The finger-held preview disc
   * and the drop that follows it must name the same cell, and the only way to guarantee that is
   * for both to come from the server's own arithmetic.
   */
  readonly landing: readonly number[];
  readonly handsToPlay: number;
  readonly hand: number;
  readonly wins: readonly [number, number];
  readonly draws: number;
  /** Who opens THIS hand — the contest's winner in hand 1, alternating after. Meaningless while
   *  {@link DoozView.openingRoll} is live (see {@link DoozState.firstMover}). */
  readonly opener: Seat;
  /** The die pair that DECIDED the match's opener, for the one-off announcement. Absent until the
   *  contest resolves, and on a fixture state. */
  readonly opening?: readonly [number, number];
  /** The LIVE opening contest — present only while it is being thrown. While it is, the board is
   *  not playable and `legalCols` is empty for both seats. */
  readonly openingRoll?: DoozOpeningRoll;
  readonly lastDrop?: DoozDrop;
  readonly moveCount: number;
  /**
   * The hand that has just been decided — while the board waits for `nextHand`, or forever on the
   * final board of a finished match. Its `line` is what the board lights.
   */
  readonly handResult?: DoozHandResult;
  /** True while `handResult` is a PAUSE rather than the end: `nextHand` is legal for both seats. */
  readonly awaitingNextHand: boolean;
  readonly status: "playing" | "over";
  /** The MATCH winner, on a decided match. Absent on a live match AND on a tied one. */
  readonly winner?: Seat;
  /** True when the finished match ended level. Mirrors `ScoreResult.tied`. */
  readonly tied?: boolean;
}
